/**
 * Viewer-local external streams (e.g. YouTube live) pinned on the Team Live
 * multiview. localStorage only — nothing is shared between viewers or
 * devices. See
 * docs/superpowers/specs/2026-08-23-team-live-external-streams-design.md
 * for the future team-wide (persisted) variant.
 */

export interface TeamExternalStream {
  /** Client-generated unique id (used as the React key). */
  id: string;
  /** 11-character YouTube video ID. */
  videoId: string;
  label?: string;
  addedAt: string;
}

const storageKey = (teamId: string) => `teamLive:externalStreams:${teamId}`;

function isExternalStream(value: unknown): value is TeamExternalStream {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    typeof v.videoId === "string" &&
    (v.label === undefined || typeof v.label === "string") &&
    typeof v.addedAt === "string"
  );
}

/** Corrupt or unknown-shape storage is treated as empty; never throws. */
export function loadTeamExternalStreams(teamId: string): TeamExternalStream[] {
  try {
    const raw = window.localStorage.getItem(storageKey(teamId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isExternalStream);
  } catch {
    return [];
  }
}

function save(teamId: string, streams: TeamExternalStream[]): void {
  try {
    window.localStorage.setItem(storageKey(teamId), JSON.stringify(streams));
  } catch {
    // Quota/private-mode failures drop persistence; the UI list still works.
  }
}

function makeId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `ext-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Appends a stream, persists, and returns the updated list. */
export function addTeamExternalStream(
  teamId: string,
  videoId: string,
  label?: string,
): TeamExternalStream[] {
  const stream: TeamExternalStream = {
    id: makeId(),
    videoId,
    ...(label ? { label } : {}),
    addedAt: new Date().toISOString(),
  };
  const next = [...loadTeamExternalStreams(teamId), stream];
  save(teamId, next);
  return next;
}

/** Removes a stream by id, persists, and returns the updated list. */
export function removeTeamExternalStream(
  teamId: string,
  id: string,
): TeamExternalStream[] {
  const next = loadTeamExternalStreams(teamId).filter((s) => s.id !== id);
  save(teamId, next);
  return next;
}
